/**
 * FetchLiveMessagesUseCase
 *
 * Paginated fetch of a live session's chat messages — the READ half the Task-7
 * `useLiveStreamingChat` hook calls to seed and page the live-chat overlay.
 * Produces `CommunityChatMessageEntity[]` newest-first (desc by
 * `createdTimestamp`) and reports whether a subsequent page exists, along with
 * the cursor (`lastTimestamp`) the hook feeds back to advance pagination. A live
 * chat message is the SAME `CommunityChatMessage` model as a community-wall
 * message; only its storage location differs (the `live_messages` collection-group
 * filtered by `parentId = liveStreamingMessageId`), so this mirrors
 * `FetchCommunityMessagesUseCase` exactly.
 *
 * Flutter source: lib/presentation/screens/community/live_streaming_screen.dart:592-620
 *   (`getMessages`) — fetch one page via
 *   `StreamingRepository().getLiveMessages(widget.liveStreamingMessageId,
 *     _lastFetchedDocument, AppConstants.communityMessagesPagination)`,
 *   append to `_messages`, update `_lastFetchedDocument`, and set `_hasMore`.
 *   The first-load `mostRecentMessage = response.messages[0]` live-cursor seed
 *   (`:611-613`) is the HOOK's concern, not this use case's.
 *   - community_data_source.dart:115 — `hasMore = querySnapshot.docs.length ==
 *     limit` (strict equality, NOT `>=`); derived in the service and forwarded
 *     unchanged here.
 *   - community_data_source.dart:95-98 — the page is ordered by
 *     `createdTimestamp` descending; the post-fetch sort below re-asserts that
 *     defensively.
 *   - app_constants.dart:11 — `messagesListPagination = 20`
 *     (`SERVER_FETCH_CONSTANTS.MESSAGES_LIST_PAGINATION`); live and wall
 *     share the same shared 20-page token.
 *
 * Pure-domain contract (conventions.md § Clean Architecture flow):
 *   - Calls `LiveStreamingService.fetchLiveMessagesPage`; does NOT touch
 *     `firestoreService` directly and does NOT dispatch to Redux.
 *   - A live session's messages are NOT end-to-end encrypted — there is no
 *     decrypt fan-out here (mirroring `FetchCommunityMessagesUseCase`).
 */

import { LiveStreamingService } from '@/domain/services/liveStreaming/liveStreamingService';
import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';
import { sortCommunityMessagesByCreatedDesc } from '@/domain/utils/communityUtils';

export interface FetchLiveMessagesResult {
  messages: CommunityChatMessageEntity[];
  /**
   * The `createdTimestamp` of the last (oldest, in this desc-ordered page)
   * document in the returned page, or `null` when the page is empty. Pass this
   * back to the next `execute` call as `lastTimestamp` to advance the cursor.
   */
  lastTimestamp: number | null;
  /**
   * `true` when the returned page is the same size as the requested `limit`
   * (so another page may exist). Mirrors `community_data_source.dart:115`.
   */
  hasMore: boolean;
}

export class FetchLiveMessagesUseCase {
  private liveStreamingService: LiveStreamingService;

  constructor(liveStreamingService: LiveStreamingService = LiveStreamingService.getInstance()) {
    this.liveStreamingService = liveStreamingService;
  }

  /**
   * Fetch one page of a live session's chat messages, newest-first.
   *
   * @param liveStreamingMessageId - The live streaming message id (`parentId` of
   *   every live message in the session).
   * @param lastTimestamp - The `createdTimestamp` of the last document from the
   *   previous page, or `null` for the first page.
   * @param limit - Page size (typically
   *   `SERVER_FETCH_CONSTANTS.MESSAGES_LIST_PAGINATION = 20`).
   */
  async execute(
    liveStreamingMessageId: string,
    lastTimestamp: number | null,
    limit: number,
  ): Promise<FetchLiveMessagesResult> {
    const { messages, lastTimestamp: nextLastTimestamp, hasMore } =
      await this.liveStreamingService.fetchLiveMessagesPage(
        liveStreamingMessageId,
        lastTimestamp,
        limit,
      );

    // Re-assert the desc-by-createdTimestamp sort defensively — mirrors
    // fetchCommunityMessagesUseCase.ts:76 and the Firestore query's
    // `orderBy("createdTimestamp", descending: true)`
    // (community_data_source.dart:95-98). Lower index = newer message, matching
    // the reversed VirtualList convention used by the community message list.
    const sorted = [...messages].sort(sortCommunityMessagesByCreatedDesc);

    // `hasMore` is already derived by the service with strict `=== limit`
    // (liveStreamingService.ts:337, parity with community_data_source.dart:115);
    // the use case forwards it unchanged.
    return {
      messages: sorted,
      lastTimestamp: nextLastTimestamp,
      hasMore,
    };
  }
}

export const fetchLiveMessagesUseCase = new FetchLiveMessagesUseCase();
